/**
 * EBL / VRM pair — the two adjustable nav lines a radar user reaches for, drawn
 * from own ship. They are a pair but each is independent: the EBL (Electronic
 * Bearing Line) is one bearing line we swing round to read off the direction of
 * something, and the VRM (Variable Range Marker) is one distance circle we grow
 * or shrink to read off how far away it is. Each has its own on/off and its own
 * value, so we can run just the bearing, just the range, or both at once.
 *
 * Kept distinct from the range-rings overlay on purpose: range rings are a fixed
 * set of evenly-spaced circles for a quick sense of scale, whereas the VRM is a
 * single circle we set to a measured distance, and the EBL has no range-rings
 * equivalent at all. They are styled differently on the chart for the same reason
 * (see eblVrmLayer), so the two are never mistaken for one another.
 *
 * Settings live on the device (localStorage), like the range-rings overlay, so a
 * setup survives a reload.
 */
import { create } from 'zustand';

const EBL_ON_LS = 'acp.eblVrm.eblOn.v1';
const EBL_BRG_LS = 'acp.eblVrm.eblBearing.v1';
const VRM_ON_LS = 'acp.eblVrm.vrmOn.v1';
const VRM_RNG_LS = 'acp.eblVrm.vrmRange.v1';
const PI_ON_LS = 'acp.eblVrm.piOn.v1';
const PI_BRG_LS = 'acp.eblVrm.piBearing.v1';
const PI_OFF_LS = 'acp.eblVrm.piOffset.v1';

function num(key: string, def: number): number {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    const n = v == null ? def : parseFloat(v);
    return Number.isFinite(n) ? n : def;
  } catch {
    return def;
  }
}
function bool(key: string, def: boolean): boolean {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    return v == null ? def : v === '1';
  } catch {
    return def;
  }
}
function save(key: string, v: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, v);
  } catch {
    /* ignore */
  }
}

/** Keep the VRM a useful, non-runaway size. */
const MIN_RANGE = 0.05;
const MAX_RANGE = 48;

/** PI offset bounds, nm. Signed: positive is starboard of the index bearing. */
const MAX_PI_OFFSET = 24;

/** Clamp a signed PI offset, keeping its side. */
export function clampPiOffset(nm: number): number {
  const mag = Math.min(MAX_PI_OFFSET, Math.max(MIN_RANGE, Math.abs(nm)));
  return nm < 0 ? -mag : mag;
}

/** Wrap a bearing into 0..359.9 so the swing is continuous past north. */
export function wrapBearing(deg: number): number {
  return ((deg % 360) + 360) % 360;
}
/** Clamp a range into sensible bounds. */
export function clampRange(nm: number): number {
  return Math.min(MAX_RANGE, Math.max(MIN_RANGE, nm));
}

interface EblVrmState {
  /** EBL (the bearing line) drawn from the boat. */
  eblOn: boolean;
  setEblOn: (b: boolean) => void;
  /** EBL bearing, degrees true (0..359.9). */
  eblBearingDeg: number;
  setEblBearingDeg: (deg: number) => void;
  /** Nudge the EBL bearing by a delta, wrapping past north. */
  nudgeEbl: (deltaDeg: number) => void;

  /** VRM (the single range circle) drawn from the boat. */
  vrmOn: boolean;
  setVrmOn: (b: boolean) => void;
  /** VRM range, nautical miles. */
  vrmRangeNm: number;
  setVrmRangeNm: (nm: number) => void;
  /** Nudge the VRM range by a delta, clamped to bounds. */
  nudgeVrm: (deltaNm: number) => void;

  /**
   * PI (parallel index): a line at a fixed bearing, offset a fixed
   * perpendicular distance from own ship, port or starboard. Steer so the
   * charted mark rides along the line and the offset IS the passing distance,
   * the radar technique on the plotter (fb-msmb1uso-6).
   */
  piOn: boolean;
  setPiOn: (b: boolean) => void;
  /** PI bearing, degrees true. The line runs both ways along this bearing. */
  piBearingDeg: number;
  setPiBearingDeg: (deg: number) => void;
  nudgePi: (deltaDeg: number) => void;
  /** Signed perpendicular offset, nm. Positive = line to starboard of the bearing. */
  piOffsetNm: number;
  setPiOffsetNm: (nm: number) => void;
}

export const useEblVrmStore = create<EblVrmState>((set) => ({
  eblOn: bool(EBL_ON_LS, false),
  setEblOn: (eblOn) => {
    save(EBL_ON_LS, eblOn ? '1' : '0');
    set({ eblOn });
  },
  eblBearingDeg: wrapBearing(num(EBL_BRG_LS, 90)),
  setEblBearingDeg: (deg) => {
    const v = wrapBearing(deg);
    save(EBL_BRG_LS, String(v));
    set({ eblBearingDeg: v });
  },
  nudgeEbl: (delta) =>
    set((s) => {
      const v = wrapBearing(s.eblBearingDeg + delta);
      save(EBL_BRG_LS, String(v));
      return { eblBearingDeg: v };
    }),

  vrmOn: bool(VRM_ON_LS, false),
  setVrmOn: (vrmOn) => {
    save(VRM_ON_LS, vrmOn ? '1' : '0');
    set({ vrmOn });
  },
  vrmRangeNm: clampRange(num(VRM_RNG_LS, 1)),
  setVrmRangeNm: (nm) => {
    const v = clampRange(nm);
    save(VRM_RNG_LS, String(v));
    set({ vrmRangeNm: v });
  },
  nudgeVrm: (delta) =>
    set((s) => {
      const v = clampRange(s.vrmRangeNm + delta);
      save(VRM_RNG_LS, String(v));
      return { vrmRangeNm: v };
    }),

  piOn: bool(PI_ON_LS, false),
  setPiOn: (piOn) => {
    save(PI_ON_LS, piOn ? '1' : '0');
    set({ piOn });
  },
  piBearingDeg: wrapBearing(num(PI_BRG_LS, 45)),
  setPiBearingDeg: (deg) => {
    const v = wrapBearing(deg);
    save(PI_BRG_LS, String(v));
    set({ piBearingDeg: v });
  },
  nudgePi: (delta) =>
    set((s) => {
      const v = wrapBearing(s.piBearingDeg + delta);
      save(PI_BRG_LS, String(v));
      return { piBearingDeg: v };
    }),
  piOffsetNm: clampPiOffset(num(PI_OFF_LS, 0.5)),
  setPiOffsetNm: (nm) => {
    const v = clampPiOffset(nm);
    save(PI_OFF_LS, String(v));
    set({ piOffsetNm: v });
  },
}));
